import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background: #ffffff;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  margin-top: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f7f7f7;
  border-bottom: 2px solid #e4e4e4;
`;

const Th = styled.th`
  padding: 1rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: #454545;
  cursor: ${props => (props.sortable ? 'pointer' : 'default')};
  user-select: none;

  &:hover {
    background: ${props => (props.sortable ? '#eeeeee' : 'transparent')};
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #e4e4e4;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9f9f9;
  }
`;

const Td = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #666666;
`;

const SortIcon = styled.span`
  margin-left: 0.5rem;
  font-size: 0.75rem;
`;

const Badge = styled.span`
  background: ${props => props.background || '#f0f0f0'};
  color: ${props => props.color || '#666666'};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const DataTable = ({ columns, data, sortable }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleSort = columnKey => {
    if (!sortable) return;

    let direction = 'asc';
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key || !sortable) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig, sortable]);

  const renderCell = (row, column) => {
    const value = row[column.key];

    if (column.render) {
      return column.render(value, row);
    }

    if (column.badge) {
      const badgeConfig = column.badge(value, row);
      return (
        <Badge background={badgeConfig.background} color={badgeConfig.color}>
          {value}
        </Badge>
      );
    }

    return value;
  };

  return (
    <TableContainer>
      <Table>
        <Thead>
          <tr>
            {columns.map(column => (
              <Th
                key={column.key}
                sortable={sortable}
                onClick={() => handleSort(column.key)}
              >
                {column.label}
                {sortable && sortConfig.key === column.key && (
                  <SortIcon>
                    {sortConfig.direction === 'asc' ? '▲' : '▼'}
                  </SortIcon>
                )}
              </Th>
            ))}
          </tr>
        </Thead>
        <Tbody>
          {sortedData.map((row, index) => (
            <Tr key={row.id || index}>
              {columns.map(column => (
                <Td key={column.key}>{renderCell(row, column)}</Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

DataTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
      badge: PropTypes.func
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  sortable: PropTypes.bool
};

DataTable.defaultProps = {
  sortable: true
};

export default DataTable;
